/**
 * API 服務層 - 統一管理前後端通訊
 */
const API_BASE = process.env.REACT_APP_API_URL || `${process.env.REACT_APP_BASE_PATH || ''}/api`;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('playerToken');
    this.adminToken = localStorage.getItem('adminToken');
  }

  async request(url, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (options.useAdminAuth && this.adminToken) {
      headers.Authorization = `Bearer ${this.adminToken}`;
    } else if (!options.skipPlayerAuth && this.token && !headers.Authorization) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const error = new Error(data.error || '請求失敗');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    // 處理 CSV 下載
    if (response.headers.get('content-type')?.includes('text/csv')) {
      return response.blob();
    }

    return response.json();
  }

  // ============ 前台 API ============

  setPlayerToken(token) {
    this.token = token;
    localStorage.setItem('playerToken', token);
  }

  clearPlayerToken() {
    this.token = null;
    localStorage.removeItem('playerToken');
  }

  async getWheelConfig() {
    return this.request('/lucky-wheel/config');
  }

  async register(data) {
    const result = await this.request('/lucky-wheel/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (result.token) {
      this.setPlayerToken(result.token);
    }
    return result;
  }

  async spin(campaignId) {
    return this.request('/lucky-wheel/spin', {
      method: 'POST',
      body: JSON.stringify({ campaignId }),
    });
  }

  async getMe() {
    return this.request('/lucky-wheel/me');
  }

  setFootballToken(token) {
    localStorage.setItem('footballToken', token);
  }

  clearFootballToken() {
    localStorage.removeItem('footballToken');
  }

  getFootballAuthHeaders() {
    const footballToken = localStorage.getItem('footballToken');
    return footballToken ? { Authorization: `Bearer ${footballToken}` } : {};
  }

  async footballRegister(data) {
    const result = await this.request('/football/register', {
      method: 'POST',
      skipPlayerAuth: true,
      body: JSON.stringify(data),
    });
    if (result.token) {
      this.setFootballToken(result.token);
    }
    return result;
  }

  async getFootballMatches() {
    return this.request('/football/matches', {
      headers: this.getFootballAuthHeaders(),
      skipPlayerAuth: true,
    });
  }

  async predictFootballMatch(matchId, selectedTeamId) {
    return this.request(`/football/matches/${matchId}/predict`, {
      method: 'POST',
      headers: this.getFootballAuthHeaders(),
      skipPlayerAuth: true,
      body: JSON.stringify({ selectedTeamId }),
    });
  }

  // ============ 後台 API ============

  setAdminToken(token) {
    this.adminToken = token;
    localStorage.setItem('adminToken', token);
  }

  clearAdminToken() {
    this.adminToken = null;
    localStorage.removeItem('adminToken');
  }

  async adminLogin(username, password) {
    const result = await this.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (result.token) {
      this.setAdminToken(result.token);
    }
    return result;
  }

  async getCampaigns() {
    return this.request('/admin/campaigns', { useAdminAuth: true });
  }

  async createCampaign(data) {
    return this.request('/admin/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async updateCampaign(id, data) {
    return this.request(`/admin/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async getPrizes(campaignId) {
    return this.request(`/admin/campaigns/${campaignId}/prizes`, { useAdminAuth: true });
  }

  async createPrize(campaignId, data) {
    return this.request(`/admin/campaigns/${campaignId}/prizes`, {
      method: 'POST',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async updatePrize(id, data) {
    return this.request(`/admin/prizes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async deletePrize(id) {
    return this.request(`/admin/prizes/${id}`, {
      method: 'DELETE',
      useAdminAuth: true,
    });
  }

  async getSpinRecords(campaignId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/campaigns/${campaignId}/records?${query}`, { useAdminAuth: true });
  }

  async exportSpinRecords(campaignId) {
    return this.request(`/admin/campaigns/${campaignId}/records/export`, { useAdminAuth: true });
  }

  async deleteSpinRecord(recordId) {
    return this.request(`/admin/records/${recordId}`, {
      method: 'DELETE',
      useAdminAuth: true,
    });
  }

  async bulkDeleteSpinRecords(ids) {
    return this.request('/admin/records/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
      useAdminAuth: true,
    });
  }

  async simulateSpin(campaignId, times) {
    return this.request(`/admin/campaigns/${campaignId}/simulate`, {
      method: 'POST',
      body: JSON.stringify({ times }),
      useAdminAuth: true,
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/admin/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
      useAdminAuth: true,
    });
  }

  async getTerms(campaignId) {
    return this.request(`/admin/campaigns/${campaignId}/terms`, { useAdminAuth: true });
  }

  async updateTerms(campaignId, terms) {
    return this.request(`/admin/campaigns/${campaignId}/terms`, {
      method: 'PUT',
      body: JSON.stringify({ terms }),
      useAdminAuth: true,
    });
  }

  async getFootballTeams() {
    return this.request('/admin/football/teams', { useAdminAuth: true });
  }

  async createFootballTeam(data) {
    return this.request('/admin/football/teams', {
      method: 'POST',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async updateFootballTeam(id, data) {
    return this.request(`/admin/football/teams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async getFootballAdminMatches() {
    return this.request('/admin/football/matches', { useAdminAuth: true });
  }

  async createFootballMatch(data) {
    return this.request('/admin/football/matches', {
      method: 'POST',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async updateFootballMatch(id, data) {
    return this.request(`/admin/football/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      useAdminAuth: true,
    });
  }

  async settleFootballMatch(id, winnerTeamId) {
    return this.request(`/admin/football/matches/${id}/settle`, {
      method: 'POST',
      body: JSON.stringify({ winnerTeamId }),
      useAdminAuth: true,
    });
  }

  async getFootballPredictions(matchId) {
    return this.request(`/admin/football/matches/${matchId}/predictions`, { useAdminAuth: true });
  }

  async getAllFootballPredictions() {
    return this.request('/admin/football/predictions', { useAdminAuth: true });
  }

  async updateFootballPredictionRewardClaimed(id, isRewardClaimed) {
    return this.request(`/admin/football/predictions/${id}/reward-claimed`, {
      method: 'PATCH',
      body: JSON.stringify({ isRewardClaimed }),
      useAdminAuth: true,
    });
  }
}

const api = new ApiService();
export default api;
